import {
  type AuditTrail,
  type Change,
  type CloneAuditTrailWithAcl,
  type CreateAuditTrailWithAcl,
  type CreateChanges,
  type HistoryEntryElementMap,
  isValueChange,
  type UpdateChanges,
} from '@openx/types/auditTrail';
import { ResourceOperation } from '@openx/types/resources';
import type { JSX } from 'react';
import { extractNormalizedCreateChange, extractNormalizedUpdateChange } from './extractNormalizedAuditTrailChange';
import { getAuditTrailReason } from './getAuditTrailReason';
import {
  AccountAcl,
  type FieldsChanges,
  type FieldsValuesMappings,
  type GroupedChanges,
  type HistoryEntriesGroup,
  type HistoryEntryData,
  type HistoryEntryDataGrouped,
  type LabeledChange,
  type ResourceLabels,
  type ValueMapping,
} from './types';

export function mapSingleValue(value?: string, mapping?: ValueMapping): string | JSX.Element {
  if (!value) {
    return '';
  }

  if (!mapping) {
    return String(value);
  }

  return (typeof mapping === 'function' ? mapping(value) : mapping[value]) || String(value);
}

export function mapMultiValue(value?: string[], mapping?: ValueMapping): (string | JSX.Element)[] {
  if (!value) {
    return [];
  }

  return value.map(singleValue => mapSingleValue(singleValue, mapping));
}

export function mapChangeValues(change: Change, mapping?: ValueMapping): Change<string | JSX.Element> {
  if (isValueChange(change)) {
    return {
      new_value: mapSingleValue(change.new_value, mapping),
      old_value: change.old_value && mapSingleValue(change.old_value, mapping),
    };
  }

  return {
    added: mapMultiValue(change.added, mapping),
    removed: mapMultiValue(change.removed, mapping),
  };
}

export const extractAclValues = (auditTrail: CreateAuditTrailWithAcl | CloneAuditTrailWithAcl): CreateChanges => {
  const changes = { ...auditTrail.changes.new_value };

  if (auditTrail.changes.new_value.acl) {
    for (const property in auditTrail.changes.new_value.acl) {
      changes[`acl.${property}`] = auditTrail.changes.new_value.acl[property].toString();
    }
  }

  return { new_value: changes };
};

export const mapAclValueToString = (changes: UpdateChanges): UpdateChanges => {
  const AclChanges = {};

  for (const permission in AccountAcl) {
    if (changes[AccountAcl[permission]]) {
      for (const property in changes[AccountAcl[permission]]) {
        AclChanges[AccountAcl[permission]] = {
          ...AclChanges[AccountAcl[permission]],
          [property]: changes[AccountAcl[permission]][property].toString(),
        };
      }

      if (!(AclChanges[AccountAcl[permission]].new_value && AclChanges[AccountAcl[permission]].old_value)) {
        if (AclChanges[AccountAcl[permission]].new_value) {
          AclChanges[AccountAcl[permission]] = {
            ...AclChanges[AccountAcl[permission]],
            old_value: '',
          };
        } else {
          AclChanges[AccountAcl[permission]] = {
            ...AclChanges[AccountAcl[permission]],
            new_value: '',
          };
        }
      }
    }
  }

  return { ...changes, ...AclChanges };
};

interface MapAuditTrailToFieldChangesParams<R extends {}> {
  auditTrail: AuditTrail;
  labels: ResourceLabels<R>;
  valuesMappings: FieldsValuesMappings<R>;
  historyEntryElementMap?: HistoryEntryElementMap;
}

export function mapAuditTrailToFieldChanges<R extends {}>({
  auditTrail,
  labels,
  valuesMappings,
  historyEntryElementMap,
}: MapAuditTrailToFieldChangesParams<R>): FieldsChanges {
  const fieldChangeExtractor =
    auditTrail.action === ResourceOperation.CREATE || auditTrail.action === ResourceOperation.CLONE
      ? (field: string) => extractNormalizedCreateChange(field, extractAclValues(auditTrail))
      : (field: string) => extractNormalizedUpdateChange(field, mapAclValueToString(auditTrail.changes));

  return Object.entries(labels)
    .map(([field, label]) => [field, label, fieldChangeExtractor(field)])
    .filter((fieldWithChange): fieldWithChange is [string, string, Change] => !!fieldWithChange[2])
    .map(
      ([field, label, change]) =>
        [
          field,
          {
            Element: historyEntryElementMap?.[field]?.({ ...change, label }),
            label,
            ...mapChangeValues(change, valuesMappings[field]),
          },
        ] as [string, LabeledChange]
    )
    .reduce((acc: FieldsChanges, [field, change]) => {
      acc[field] = change;
      return acc;
    }, {});
}

interface MapAuditTrailToHistoryEntryParams<R extends {}> {
  auditTrail: AuditTrail;
  labels: ResourceLabels<R>;
  valuesMappings: FieldsValuesMappings<R>;
  keepOriginalForCreate?: boolean;
  historyEntryElementMap?: HistoryEntryElementMap;
}

export function mapAuditTrailToHistoryEntry<R extends {}>({
  auditTrail,
  labels,
  valuesMappings,
  keepOriginalForCreate,
  historyEntryElementMap,
}: MapAuditTrailToHistoryEntryParams<R>): HistoryEntryData {
  const who = getAuditTrailReason(auditTrail);
  const when = auditTrail.timestamp;
  const mode = auditTrail.action;
  const objectId = auditTrail.obj_id;
  const traceId = auditTrail.trace_id;

  const what =
    keepOriginalForCreate && mode !== 'update' && mode !== 'expire'
      ? auditTrail.changes.new_value
      : mapAuditTrailToFieldChanges({ auditTrail, historyEntryElementMap, labels, valuesMappings });

  return {
    mode,
    objectId,
    what,
    when,
    who,
    traceId,
  };
}

export function groupHistoryEntries(entry: HistoryEntryData, groups: HistoryEntriesGroup[]): HistoryEntryDataGrouped {
  const result: HistoryEntryDataGrouped = { ...entry, what: [] };

  const groupedChanges = groups.map(({ groupTitle, keys }) => {
    const groupChanges: GroupedChanges = { changes: [], groupTitle };
    keys.forEach(key => {
      if (entry.what[key]) {
        groupChanges.changes.push(entry.what[key]);
      }
    });

    return groupChanges;
  });

  result.what = groupedChanges.filter(group => !!group.changes.length);

  return result;
}
